/**
 * @fileoverview Custom Chart Tooltip Component
 * 
 * @description
 * Tooltip customizado para Recharts com acessibilidade WCAG AA.
 * Usa cores light (#F8F8F2, #D1D5DB) em fundo dark (#1E2329) para contraste 6:1+.
 * 
 * @version 1.0.0
 * @see PLANO_P0_1_KE_WACC.md - Tooltip contrast requirements
 */

import React from 'react';
import { Box, Typography, Paper } from '@mui/material';

// ============================================================================
// TYPES
// ============================================================================

export interface CustomChartTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    dataKey: string;
  }>;
  label?: string;
  labelFormatter?: (label: string) => string;
  valueFormatter?: (value: number, name?: string) => string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Custom Tooltip for Recharts
 * 
 * @example
 * ```tsx
 * <RechartsTooltip 
 *   content={<CustomChartTooltip 
 *     labelFormatter={(l) => `Ano ${l}`}
 *     valueFormatter={(v) => formatCurrency(v)}
 *   />} 
 * />
 * ```
 */
export const CustomChartTooltip: React.FC<CustomChartTooltipProps> = ({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
}) => {
  // Não renderiza se inativo ou sem dados
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={8}
      sx={{
        backgroundColor: 'background.paper', // Dark background (from design-tokens)
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        minWidth: '180px',
        maxWidth: '320px',
      }}
    >
      {/* Label (e.g., "Ano 2025") */}
      {label && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.primary', // Light white (WCAG AA: 12:1 contrast)
            fontWeight: 600,
            marginBottom: '8px',
            display: 'block',
            fontSize: '0.75rem', // 12px
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}
        >
          {labelFormatter ? labelFormatter(label) : label}
        </Typography>
      )}

      {/* Values */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {payload.map((entry, index) => (
          <Box
            key={`item-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            {/* Color indicator + Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              <Box
                sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '2px',
                  backgroundColor: entry.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary', // Light gray (WCAG AA: 6.5:1 contrast)
                  fontSize: '0.875rem', // 14px
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {entry.name}
              </Typography>
            </Box>

            {/* Value */}
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary', // Light white (WCAG AA: 12:1 contrast)
                fontSize: '0.875rem', // 14px
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {valueFormatter 
                ? valueFormatter(entry.value, entry.name) 
                : entry.value.toLocaleString('pt-BR')
              }
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

/**
 * ACCESSIBILITY NOTES:
 * 
 * 1. **Contrast Ratios** (WCAG AA 4.5:1 minimum):
 *    - Label (#F8F8F2 on #1E2329): 12:1 ✅
 *    - Name (#D1D5DB on #1E2329): 6.5:1 ✅
 *    - Value (#F8F8F2 on #1E2329): 12:1 ✅
 * 
 * 2. **Visual Hierarchy**:
 *    - Label: UPPERCASE, 12px, 600 weight (most prominent)
 *    - Name: Regular case, 14px, 400 weight (secondary)
 *    - Value: Regular case, 14px, 600 weight (emphasis)
 * 
 * 3. **Color Indicators**:
 *    - 12px square with scenario color (pessimista/base/otimista)
 *    - Not solely relying on color (text labels present)
 * 
 * 4. **Responsive**:
 *    - minWidth: 180px (mobile readable)
 *    - maxWidth: 320px (prevents overflow)
 *    - Text ellipsis for long names
 * 
 * 5. **Elevation**:
 *    - elevation={8} for depth perception
 *    - Border for better definition against charts
 */
